import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGift,
  faUsers,
  faStore,
  faChevronDown,
  faChartLine,
  faCoins,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";

function VisiGiftDashboard() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [visitorsOpen, setVisitorsOpen] = useState(false);
  const [exhibitorsOpen, setExhibitorsOpen] = useState(false);
  const [merchantsOpen, setMerchantsOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/visigift");
  };

  const toggleVisitors = () => {
    setVisitorsOpen(!visitorsOpen);
  };

  const toggleExhibitors = () => {
    setExhibitorsOpen(!exhibitorsOpen);
  };

  const toggleMerchants = () => {
    setMerchantsOpen(!merchantsOpen);
  };

  const sidebarItems = {
    visitors: [
      "Visit Diary",
      "Discount & Offer History",
      "Call History",
      "Watch History",
      "Points History - Kitna Hua",
      "Cash History - Kitna Mila",
      "App Download History",
      "Scratch History",
      "Gift History",
      "Shopping History",
      "Form Fillup History",
      "Brochure Download History",
    ],
    exhibitors: [
      "Visitors Diary",
      "Discount & Offer History",
      "Call History",
      "Watch History",
      "Points History - Kitna Hua",
      "Cash History - Kitna Mila",
      "App Download History",
      "Scratch History",
      "Gift History",
      "Shopping History",
      "Form Fillup History",
      "Brochure Download History",
    ],
    merchants: [
      "Visitors Diary",
      "Discount & Offer History",
      "Call History",
      "Watch History",
      "Chat History",
      "Spend History",
      "App Download History",
      "Scratch History",
      "Gift History",
      "Shopping History",
      "Form Fillup History",
      "Brochure Download History",
      "Create Campaign",
      "Activate Campaign",
      "Deactivate Campaign",
      "Budget & Payment",
      "Merchant Rules",
      "Approve Exhibitor",
      "Associate Exhibitor",
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#12001a] via-[#1a0b1e] to-[#1b002a] text-gray-200 font-sans">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-sm border-b border-purple-700 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 text-white text-2xl font-extrabold tracking-wide">
            <FontAwesomeIcon icon={faGift} className="text-purple-400" />
            <span>VisiGift</span>
          </div>
          <div className="text-xs text-yellow-300 font-light">
            Rewarding Ads & Videos – Earn While You Watch & Shop Smart!
          </div>
        </div>

        {/* Desktop User Controls */}
        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <span className="text-sm text-gray-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="px-2 py-1 rounded-md bg-gradient-to-r from-red-700 to-red-600 text-white text-sm font-semibold hover:scale-105 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-md border border-purple-600 text-sm hover:bg-purple-800 transition"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-3 py-1 rounded-md bg-gradient-to-r from-purple-700 to-purple-600 text-white text-sm font-semibold hover:scale-105 transition"
              >
                Register
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setIsMenuOpen((prev) => !prev)}
          className="md:hidden w-9 h-9 flex items-center justify-center text-xl text-white bg-purple-700 rounded-lg hover:bg-purple-800 transition"
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* DROPDOWNS SECTION BELOW HEADER */}
      <section className="flex bg-black/50 border-b border-purple-700 px-4 py-2">
        <div className="flex flex-row justify-center items-center space-y-2">
          {[
            { label: "Visitors", icon: faUsers, open: visitorsOpen, setOpen: toggleVisitors, items: sidebarItems.visitors },
            { label: "Exhibitors", icon: faChartLine, open: exhibitorsOpen, setOpen: toggleExhibitors, items: sidebarItems.exhibitors },
            { label: "Merchants", icon: faStore, open: merchantsOpen, setOpen: toggleMerchants, items: sidebarItems.merchants },
          ].map(({ label, icon, open, setOpen, items }, idx) => (
            <div key={idx} className="border border-purple-700 rounded overflow-hidden bg-black/30">
              <button
                onClick={setOpen}
                className="w-full flex items-center justify-between p-3 text-left font-semibold text-gray-200 hover:bg-purple-900/30 transition"
              >
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon={icon} className="text-purple-400 text-sm" />
                  {label}
                </span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`transition-transform text-purple-400 text-sm ${open ? "rotate-180" : ""}`}
                />
              </button>
              {open && (
                <ul className="py-1 space-y-1 max-h-48 overflow-y-auto">
                  {items.map((item, itemIdx) => (
                    <li
                      key={itemIdx}
                      className="px-4 py-2 text-sm text-gray-300 hover:bg-purple-800/50 hover:text-white transition cursor-pointer"
                    >
                      {item}
                </li>
                ))}
                </ul>
              )}
            </div>
            
          ))}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="flex-1 p-4 md:p-6">
        <div className="text-center mb-4">
          <p className="text-white text-2xl font-bold mb-2 drop-shadow-lg">
            Rewarding Your Screen Time with Think5Whys 🎯
          </p>
          <p className="text-sm text-gray-400">
            Engage. Watch. Earn. Shop Smart with VisiGift!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: "Total Points", value: "1,250", color: "text-yellow-400", icon: faCoins },
            { title: "Daily Streak", value: "7 days", color: "text-green-400", icon: faBolt },
            { title: "Pending Rewards", value: "3", color: "text-gray-300", icon: faGift },
          ].map((card, idx) => (
            <div
              key={idx}
              className="bg-black/70 border border-purple-700 rounded-xl p-4 flex flex-col items-center justify-center shadow-lg hover:shadow-purple-800/40 hover:scale-[1.02] transition-transform"
            >
              <FontAwesomeIcon icon={card.icon} className={`${card.color} text-2xl mb-2`} />
              <h3 className="text-sm font-semibold text-gray-300">{card.title}</h3>
              <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
            </div>
          ))}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="bg-black/80 border-t border-purple-700 text-gray-400 py-3 text-center text-xs md:text-sm mt-auto">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2 px-3">
          <span>© 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</span>
          <div className="flex flex-wrap gap-3">
            <Link to="/about-us" className="hover:text-purple-400">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-purple-400">
              Privacy
            </Link>
            <Link to="/terms-of-use" className="hover:text-purple-400">
              Terms
            </Link>
            <Link to="/contact-us" className="hover:text-purple-400">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default VisiGiftDashboard;